import {
  Button,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import {
  CircleDollarSign,
  Check,
  Loader2,
  Package,
  PackageCheck,
  Search,
  Tag,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type {
  CatalogProductListItem,
  CatalogSkuDTO,
} from "@/modules/catalog/presentation/api/client";
import { catalogApi } from "@/modules/catalog/presentation/api/client";
import type { ShopProductAssignBody } from "@/modules/shops/domain/contracts";
import {
  DEFAULT_SHOP_CURRENCY,
  isShopCurrency,
  type ShopCurrency,
} from "@/shared/lib/currency";
import { FieldHelpHint } from "@/shared/ui/FieldHelpHint";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

const PRODUCT_PAGE_SIZE = 20;

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK (₭)" },
  { value: "THB", label: "THB (฿)" },
  { value: "USD", label: "USD ($)" },
];

function currencySymbol(currency: ShopCurrency): string {
  if (currency === "THB") return "฿";
  if (currency === "USD") return "$";
  return "₭";
}

const AssignSkuRowSchema = z.object({
  catalogSkuId: z.string().uuid(),
  sku: z.string().min(1, "ຕ້ອງໃສ່ SKU ຂອງຮ້ານ"),
  barcode: z.string().optional().default(""),
  price: z.coerce.number().min(0, "ລາຄາຕ້ອງບໍ່ຕ່ຳກວ່າ 0"),
  cost: z.union([z.coerce.number().min(0), z.literal("")]).optional(),
  currency: z.enum(["LAK", "THB", "USD"]),
});

const AssignFormSchema = z.object({
  skus: z.array(AssignSkuRowSchema).min(1, "ຕ້ອງມີຢ່າງໜ້ອຍ 1 SKU"),
});

type AssignFormValues = z.infer<typeof AssignFormSchema>;

function productLookupQueryFn(params: {
  search: string;
  pageParam: number;
}): Promise<{ items: CatalogProductListItem[]; nextPage: number | null }> {
  const { search, pageParam } = params;
  return catalogApi
    .listProducts({
      limit: PRODUCT_PAGE_SIZE,
      offset: (pageParam - 1) * PRODUCT_PAGE_SIZE,
      search: search.trim() || undefined,
    })
    .then((res) => ({
      items: res.data,
      nextPage:
        res.meta.offset + res.data.length < res.meta.total
          ? pageParam + 1
          : null,
    }));
}

async function productPreloadQueryFn(
  id: string,
): Promise<CatalogProductListItem | null> {
  if (!id) return null;
  try {
    const p = await catalogApi.getProduct(id);
    return {
      id: p.id,
      name: p.name,
      type: p.type,
      status: p.status,
      categoryId: p.categoryId,
      brandId: p.brandId,
      categoryName: p.categoryName,
      brandName: p.brandName,
      thumbnailUrl: null,
      updatedAt: p.updatedAt,
    };
  } catch {
    return null;
  }
}

function productLabel(item: CatalogProductListItem): string {
  const meta = [item.brandName, item.categoryName].filter(Boolean).join(" · ");
  return meta ? `${item.name} (${meta})` : item.name;
}

function StepBadge({
  n,
  label,
  active,
  done,
}: {
  n: number;
  label: string;
  active: boolean;
  done: boolean;
}) {
  return (
    <div
      className={[
        "flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3 py-2",
        active
          ? "border-[#F26522]/40 bg-[#F26522]/8"
          : done
            ? "border-border bg-muted/40"
            : "border-border/60 bg-background opacity-60",
      ].join(" ")}
    >
      <span
        className={[
          "flex size-6 shrink-0 items-center justify-center rounded-full font-semibold text-xs",
          active
            ? "bg-[#F26522] text-white"
            : done
              ? "bg-muted-foreground/20 text-foreground"
              : "bg-muted text-muted-foreground",
        ].join(" ")}
      >
        {done && !active ? <Check className="size-3.5" aria-hidden /> : n}
      </span>
      <span
        className={[
          "truncate font-medium text-sm",
          active ? "text-foreground" : "text-muted-foreground",
        ].join(" ")}
      >
        {label}
      </span>
    </div>
  );
}

function SkuPriceFields({
  index,
  currencyLocked,
  lockedCurrency,
}: {
  index: number;
  currencyLocked: boolean;
  lockedCurrency: ShopCurrency;
}) {
  const watched = RHF.useWatch({ name: `skus.${index}.currency` }) as
    | ShopCurrency
    | undefined;
  const cur: ShopCurrency =
    currencyLocked || !isShopCurrency(String(watched ?? ""))
      ? lockedCurrency
      : watched!;
  const symbol = currencySymbol(cur);

  return (
    <>
      <FormPrice
        name={`skus.${index}.price`}
        label="ລາຄາຂາຍ"
        requiredMark
        currencySymbol={symbol}
        placeholder="0"
      />
      <FormPrice
        name={`skus.${index}.cost`}
        label="ຕົ້ນທຶນ (ທາງເລືອກ)"
        currencySymbol={symbol}
        placeholder="—"
      />
    </>
  );
}

type AssignProductModalProps = {
  shopId: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (body: ShopProductAssignBody) => Promise<void>;
  alreadyAssignedProductIds: string[];
  shopDefaultCurrency?: string;
  enforceSingleShopSkuCurrency?: boolean;
};

export function AssignProductModal({
  open,
  onClose,
  onSubmit,
  alreadyAssignedProductIds,
  shopDefaultCurrency,
  enforceSingleShopSkuCurrency = false,
}: AssignProductModalProps) {
  const [productId, setProductId] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<CatalogProductListItem | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const rawDefault = String(shopDefaultCurrency ?? "");
  const lockedCurrency: ShopCurrency = isShopCurrency(rawDefault)
    ? rawDefault
    : DEFAULT_SHOP_CURRENCY;
  const skuCurrencyLocked = enforceSingleShopSkuCurrency;

  const methods = RHF.useForm<AssignFormValues>({
    resolver: zodResolver(AssignFormSchema),
    defaultValues: { skus: [] },
  });

  const { fields } = RHF.useFieldArray({
    control: methods.control,
    name: "skus",
  });

  const { data: skuList, isLoading: skusLoading } = useQuery({
    queryKey: ["catalog", "products", productId, "skus"],
    queryFn: () => catalogApi.listSkus(productId, { limit: 50 }),
    enabled: !!productId && open,
  });

  const skuById = useMemo(() => {
    const map = new Map<string, CatalogSkuDTO>();
    for (const s of skuList?.data ?? []) map.set(s.id, s);
    return map;
  }, [skuList]);

  const isAlreadyAssigned = Boolean(
    productId && alreadyAssignedProductIds.includes(productId),
  );
  const hasSkus = fields.length > 0 && !isAlreadyAssigned;
  const step2Ready = Boolean(productId) && !isAlreadyAssigned && !skusLoading;

  useEffect(() => {
    const data = skuList?.data;
    if (!productId || !data?.length) {
      if (productId && data && data.length === 0) {
        methods.reset({ skus: [] });
      }
      return;
    }
    methods.reset({
      skus: data.map((cs: CatalogSkuDTO) => ({
        catalogSkuId: cs.id,
        sku: cs.oemPartNo?.trim() ?? "",
        barcode: "",
        price: 0,
        cost: "" as const,
        currency: lockedCurrency,
      })),
    });
  }, [productId, skuList, methods, lockedCurrency]);

  const handleClose = () => {
    setProductId("");
    setSelectedProduct(null);
    methods.reset({ skus: [] });
    setError("");
    onClose();
  };

  const handleProductChange = (id: string) => {
    setProductId(id);
    setError("");
    if (!id) {
      setSelectedProduct(null);
      methods.reset({ skus: [] });
    }
  };

  const handleSubmit = async (vals: AssignFormValues) => {
    if (!productId || isAlreadyAssigned) {
      setError("ຮ້ານນີ້ມີສິນຄ້ານີ້ແລ້ວ");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const body: ShopProductAssignBody = {
        catalogProductId: productId,
        skus: vals.skus.map((r) => ({
          catalogSkuId: r.catalogSkuId,
          sku: r.sku.trim(),
          barcode: r.barcode?.trim() || undefined,
          price: r.price,
          cost:
            r.cost !== undefined && r.cost !== "" && r.cost !== null
              ? Number(r.cost)
              : undefined,
          currency: skuCurrencyLocked ? lockedCurrency : r.currency,
        })),
      };
      await onSubmit(body);
      handleClose();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? "ເກີດຂໍ້ຜິດພາດ";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-xs">
        {hasSkus
          ? `${fields.length} SKU · ຕ້ອງໃສ່ລາຄາ ແລະ SKU ຂອງຮ້ານ`
          : "ເລືອກສິນຄ້າຈາກ Catalog ກ່ອນ"}
      </p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={handleClose}>
          ຍົກເລີກ
        </Button>
        <Button
          type="button"
          className="inline-flex items-center gap-2 bg-[#F26522] hover:bg-[#F26522]/90"
          disabled={!hasSkus || submitting}
          onClick={() => void methods.handleSubmit(handleSubmit)()}
        >
          {submitting ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              ກຳລັງບັນທຶກ...
            </>
          ) : (
            <>
              <PackageCheck className="size-4" />
              ມອບສິນຄ້າ
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (!o ? handleClose() : undefined)}
      title={
        <span className="flex items-center gap-2">
          <span className="flex size-8 items-center justify-center rounded-lg bg-[#F26522]/12 text-[#F26522]">
            <Package className="size-4" aria-hidden />
          </span>
          ມອບສິນຄ້າໃຫ້ຮ້ານ
        </span>
      }
      description="ເລືອກສິນຄ້າຈາກ Catalog ແລ້ວຕັ້ງ SKU / ລາຄາສຳລັບຮ້ານນີ້"
      size="lg"
      footer={footer}
      className={[
        "flex max-h-[min(90dvh,760px)] flex-col gap-4 overflow-hidden",
        "[&>[data-slot=dialog-header]]:shrink-0",
        "[&>[data-slot=dialog-footer]]:shrink-0",
      ].join(" ")}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1">
        <FormRoot<AssignFormValues>
          methods={methods}
          onSubmit={handleSubmit}
          className="space-y-5"
        >
        <div className="flex gap-2">
          <StepBadge
            n={1}
            label="ເລືອກສິນຄ້າ"
            active={!step2Ready}
            done={Boolean(productId) && !isAlreadyAssigned}
          />
          <StepBadge
            n={2}
            label="ຕັ້ງຄ່າ SKU / ລາຄາ"
            active={step2Ready}
            done={false}
          />
        </div>

        <section className="space-y-3">
          <div className="flex items-start gap-2.5">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Search className="size-4" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-foreground text-sm">
                ຄົ້ນຫາສິນຄ້າໃນ Catalog
              </h3>
              <p className="mt-0.5 text-muted-foreground text-xs">
                ສິນຄ້າທີ່ມອບໃຫ້ຮ້ານນີ້ແລ້ວຈະເລືອກບໍ່ໄດ້
              </p>
            </div>
          </div>

          <InfiniteCombobox<CatalogProductListItem>
            queryKey={["catalog", "products", "lookup"]}
            queryFn={productLookupQueryFn}
            preloadQueryFn={productPreloadQueryFn}
            getLabel={productLabel}
            getValue={(item) => item.id}
            getDisabled={(item) =>
              alreadyAssignedProductIds.includes(item.id)
            }
            value={productId}
            onValueChange={(id) => {
              handleProductChange(id);
              if (id) {
                void productPreloadQueryFn(id).then((p) =>
                  setSelectedProduct(p),
                );
              }
            }}
            placeholder="ພິມຊື່ສິນຄ້າ, ແບຣນ..."
            clearable
          />

          {isAlreadyAssigned ? (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-sm">
              ຮ້ານນີ້ມີສິນຄ້ານີ້ໃນລາຍການແລ້ວ — ເລືອກສິນຄ້າອື່ນ
            </p>
          ) : null}

          {selectedProduct && productId && !isAlreadyAssigned ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-muted/25 px-3 py-2.5">
              <div className="min-w-0">
                <p className="font-medium text-foreground text-sm leading-snug">
                  {selectedProduct.name}
                </p>
                <p className="mt-0.5 text-muted-foreground text-xs">
                  {[selectedProduct.brandName, selectedProduct.categoryName]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
              </div>
            </div>
          ) : null}
        </section>

        {skusLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground text-sm">
            <Loader2 className="size-4 animate-spin" />
            ກຳລັງໂຫຼດ SKU...
          </div>
        ) : null}

        {!skusLoading &&
        productId &&
        !isAlreadyAssigned &&
        fields.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border px-3 py-8 text-center text-muted-foreground text-sm">
            ບໍ່ພົບ SKU ສຳລັບສິນຄ້ານີ້ໃນ Catalog
          </p>
        ) : null}

        {hasSkus ? (
          <section className="space-y-3">
            <div className="flex items-start gap-2.5">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <Tag className="size-4" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-foreground text-sm">
                  ຕັ້ງຄ່າ SKU ຕໍ່ຮ້ານ
                </h3>
                <p className="mt-0.5 text-muted-foreground text-xs">
                  ໃສ່ລະຫັດຮ້ານ ແລະ ລາຄາຂາຍ — ຕົ້ນທຶນວາງໄດ້
                </p>
              </div>
            </div>

            {skuCurrencyLocked ? (
              <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 px-3 py-2 text-sm">
                <CircleDollarSign className="size-4 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">ສະກຸນເງິນ:</span>
                <span className="font-mono font-medium">{lockedCurrency}</span>
                <span className="text-muted-foreground text-xs">
                  (ລັອກຕາມຕັ້ງຄ່າຮ້ານ)
                </span>
              </div>
            ) : null}

            <div className="space-y-3">
              {fields.map((field, i) => {
                const meta = skuById.get(field.catalogSkuId);
                const oem = meta?.oemPartNo?.trim();
                const condition = meta?.condition;
                return (
                  <div
                    key={field.id}
                    className="space-y-3 rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-border/70 border-b pb-2">
                      <div className="min-w-0">
                        <p className="font-medium text-foreground text-sm">
                          SKU #{i + 1}
                          {oem ? (
                            <span className="ml-2 font-normal text-muted-foreground">
                              OEM: {oem}
                            </span>
                          ) : null}
                        </p>
                        {condition ? (
                          <p className="text-muted-foreground text-xs capitalize">
                            ສະພາບ: {condition}
                          </p>
                        ) : null}
                      </div>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <FormInput
                        name={`skus.${i}.sku`}
                        label={
                          <FieldHelpHint label="SKU ຂອງຮ້ານ">
                            ລະຫັດສິນຄ້າຂອງຮ້ານ — ໃຊ້ໃນ POS / ສະແກນ / ຄົ້ນຫາ.
                            ແຕກຕ່າງຈາກ OEM Part No ຂອງ catalog.
                          </FieldHelpHint>
                        }
                        placeholder="ລະຫັດ SKU"
                        requiredMark
                        className="mb-0"
                      />
                      <FormInput
                        name={`skus.${i}.barcode`}
                        label="Barcode"
                        placeholder="ວ່າງໄດ້"
                        className="mb-0"
                      />
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2">
                      <SkuPriceFields
                        index={i}
                        currencyLocked={skuCurrencyLocked}
                        lockedCurrency={lockedCurrency}
                      />
                    </div>

                    {skuCurrencyLocked ? (
                      <input
                        type="hidden"
                        {...methods.register(`skus.${i}.currency`)}
                      />
                    ) : (
                      <FormSelect
                        name={`skus.${i}.currency`}
                        label="ສະກຸນເງິນ"
                        options={CURRENCY_OPTIONS}
                        className="mb-0 max-w-xs"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {error ? (
          <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-destructive text-sm">
            {error}
          </p>
        ) : null}
        </FormRoot>
      </div>
    </Modal>
  );
}
